import { Box, Button, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { Check, Feed, GridView, PersonAdd, Task as TaskIcon } from "@mui/icons-material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import StudentsForm from "../Students/StudentsForm";
import TaskForm from "../Tasks/TaskForm";
import SubmissionForm from "../Submissions/SubmissionForm";
import { useGetUsersQuery } from "../../services/users.service";
import { useCreateStudentMutation } from "../../services/students.service";
import { useCreateTaskMutation } from "../../services/tasks.service";
import { useCreateSubmissionMutation } from "../../services/submissions.service";
import { useAppTheme } from "../../context/ThemeContext";

interface Props {
  editMode: boolean;
  onToggleEdit: () => void;
}

function GreetingHeader({ editMode, onToggleEdit }: Props) {
  const username = useSelector((state: RootState) => state.user.username);
  const { data: users } = useGetUsersQuery("users");
  const [createStudent] = useCreateStudentMutation();
  const [createTask] = useCreateTaskMutation();
  const [createSubmission] = useCreateSubmissionMutation();
  const { accentColor } = useAppTheme();

  const [openStudent, setOpenStudent] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [openSubmission, setOpenSubmission] = useState(false);

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      {openStudent && (
        <StudentsForm
          open
          edit={false}
          selectedStudent={null}
          users={users ?? []}
          handleClose={() => setOpenStudent(false)}
          submitHanlder={(s: any) => createStudent(s).then(() => setOpenStudent(false))}
        />
      )}
      {openTask && (
        <TaskForm
          open
          edit={false}
          selectedTask={null}
          users={users ?? []}
          handleClose={() => setOpenTask(false)}
          submitHanlder={(t: any) => createTask(t).then(() => setOpenTask(false))}
        />
      )}
      {openSubmission && (
        <SubmissionForm
          open
          edit={false}
          selectedSubmission={null}
          users={users ?? []}
          handleClose={() => setOpenSubmission(false)}
          submitHanlder={(s: any) => createSubmission(s).then(() => setOpenSubmission(false))}
        />
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
            {greeting}, {username}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            {dateStr}
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
            onClick={() => setOpenStudent(true)}
            sx={{ fontSize: "0.8rem", fontWeight: 600, height: 34, textTransform: "none" }}
          >
            Student
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Feed sx={{ fontSize: 16 }} />}
            onClick={() => setOpenSubmission(true)}
            sx={{ fontSize: "0.8rem", fontWeight: 600, height: 34, textTransform: "none" }}
          >
            Submission
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<TaskIcon sx={{ fontSize: 16 }} />}
            onClick={() => setOpenTask(true)}
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              height: 34,
              textTransform: "none",
              bgcolor: accentColor,
              "&:hover": { bgcolor: accentColor, opacity: 0.9 },
            }}
          >
            Task
          </Button>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 22, alignSelf: "center" }} />

          <Tooltip title={editMode ? "Save layout" : "Customize layout"} arrow>
            <Button
              size="small"
              variant={editMode ? "contained" : "outlined"}
              startIcon={
                editMode
                  ? <Check sx={{ fontSize: 15 }} />
                  : <GridView sx={{ fontSize: 15 }} />
              }
              onClick={onToggleEdit}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                height: 34,
                textTransform: "none",
                ...(editMode && {
                  bgcolor: accentColor,
                  "&:hover": { bgcolor: accentColor, opacity: 0.9 },
                }),
              }}
            >
              {editMode ? "Done" : "Customize"}
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </>
  );
}

export default GreetingHeader;
